import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import * as _redux from './setup';
import store, { persistor } from './setup/redux/Store';
import axios from 'axios';
import { Chart, registerables } from 'chart.js';
import { App } from './app/App';
import { FlexChargeI18nProvider } from './setup/i18n/FlexChargei18n';
import 'rsuite/dist/rsuite.min.css';
import './_flexCharge/assets/sass/style.scss';
import './_flexCharge/assets/sass/style.react.scss';

const { PUBLIC_URL } = process.env;

_redux.setupAxios(axios, store);

Chart.register(...registerables);

Sentry.init({
	dsn: 'https://69e8f676367864fc09d47b96e8ef16b7@o1378422.ingest.us.sentry.io/4509440808452096',
	release: `portal@${process.env.REACT_APP_ENVIRONMENT}`,
	integrations: [],
	tracesSampleRate: 1.0,
	environment: `portal-${process.env.REACT_APP_ENVIRONMENT}`,
	beforeSend: (event, hint) => {
		try {
			// Only send events in production
			if (process.env.REACT_APP_ENVIRONMENT !== 'production' && process.env.REACT_APP_ENVIRONMENT !== 'sandbox') {
				return null; // Don't send the event
			}

			// Skip 401 errors
			const error = hint?.originalException as any;
			if (error && typeof error === 'object') {
				// Check for 401 errors
				if (
					error.status === 401 ||
					error.statusCode === 401 ||
					error.message === 'Request failed with status code 401' ||
					(error.response && error.response.status === 401)
				) {
					return null; // Don't send the event
				}

				// Skip canceled requests
				if (
					error.message === 'canceled' ||
					(typeof error.message === 'string' && error.message.includes('canceled')) ||
					error.code === 'ERR_CANCELED' ||
					error.name === 'CanceledError' ||
					error.message === 'The user aborted a request'
				) {
					return null; // Don't send the event
				}
			}

			return event; // Send the event
		} catch (e) {
			// If anything goes wrong in our filtering logic,
			// log it and return the original event to be safe
			console.error('Error in Sentry beforeSend:', e);
			return event;
		}
	}
});

Sentry.setTag('environment', 'portal-' + process.env.REACT_APP_ENVIRONMENT);
Sentry.setTag('release', 'portal-' + process.env.REACT_APP_VERSION);
Sentry.setTag('widget', 'portal');

if (process.env.NODE_ENV === 'production') {
	console.log = (): void => {};
	console.error = (): void => {};
	console.debug = (): void => {};
}

window.addEventListener('unhandledrejection', event => {
	console.error('Unhandled promise rejection:', event.reason);

	// Send to Sentry if it's not already being tracked
	if (process.env.REACT_APP_ENVIRONMENT === 'production') {
		Sentry.captureException(event.reason || new Error('Unhandled Promise rejection'));
	}

	// Prevent the default browser behavior (console error)
	event.preventDefault();
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
	<FlexChargeI18nProvider>
		{/*@ts-ignore*/}
		<Provider store={store}>
			<PersistGate persistor={persistor} loading={<div>Loading...</div>}>
				<App basename={PUBLIC_URL} />
			</PersistGate>
		</Provider>
	</FlexChargeI18nProvider>
);
